/**
 * 前端时间工具函数
 * 统一处理中国时区时间，确保无论客户端在哪个时区都使用中国时间
 */

/**
 * 获取中国时区当前时间字符串
 * 格式：YYYY-MM-DD HH:mm:ss
 */
export function getChinaDateTime(): string {
  return new Date().toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-').replace(',', '');
}

/**
 * 获取中国时区当前日期字符串
 * 格式：YYYY-MM-DD
 */
export function getChinaDate(): string {
  return new Date().toLocaleDateString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '-');
}

/**
 * 获取中国时区当前时间字符串
 * 格式：HH:mm
 */
export function getChinaTime(): string {
  return new Date().toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * 创建中国时区的Date对象
 * @param dateString 可选的日期字符串，如果不提供则使用当前时间
 */
export function createChinaDate(dateString?: string): Date {
  if (dateString) {
    // 如果是已有的时间字符串，先转换为UTC，再考虑中国时区
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${dateString}`);
    }
    return date;
  }
  
  // 如果没有参数，返回当前中国时区时间对应的UTC时间
  const now = new Date();
  const chinaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
  return chinaTime;
}

/**
 * 获取中国时区指定日期的开始时间（00:00:00）
 * @param daysOffset 相对于今天的偏移天数，负数表示过去
 */
export function getChinaDateStart(daysOffset: number = 0): Date {
  const chinaToday = getChinaDate();
  const targetDate = new Date(chinaToday);
  targetDate.setDate(targetDate.getDate() + daysOffset);
  targetDate.setHours(0, 0, 0, 0);
  return targetDate;
}

/**
 * 获取中国时区指定日期的结束时间（23:59:59）
 * @param daysOffset 相对于今天的偏移天数，负数表示过去
 */
export function getChinaDateEnd(daysOffset: number = 0): Date {
  const chinaToday = getChinaDate();
  const targetDate = new Date(chinaToday);
  targetDate.setDate(targetDate.getDate() + daysOffset);
  targetDate.setHours(23, 59, 59, 999);
  return targetDate;
}

/**
 * 格式化日期为输入框使用的格式（YYYY-MM-DD）
 * @param date Date对象或日期字符串
 */
export function formatDateForInput(date: Date | string): string {
  let targetDate: Date;
  
  if (typeof date === 'string') {
    targetDate = new Date(date);
  } else {
    targetDate = date;
  }
  
  // 转换为中国时区的日期字符串
  return targetDate.toLocaleDateString('sv-SE', {
    timeZone: 'Asia/Shanghai'
  });
}

/**
 * 将任意时间字符串转换为中国时区时间显示
 * @param timeString 时间字符串
 * @returns 中国时区时间字符串
 */
export function formatToChinaTime(timeString: string): string {
  const date = new Date(timeString);
  return date.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-').replace(',', '');
}

/**
 * 格式化时间显示（仅时分）
 * @param timeString 时间字符串
 * @returns HH:mm 格式
 */
export function formatTimeDisplay(timeString: string): string {
  const date = new Date(timeString);
  return date.toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * 判断时间字符串是否为今天（中国时区）
 * @param timeString 时间字符串
 */
export function isToday(timeString: string): boolean {
  const date = new Date(timeString);
  const today = getChinaDate();
  const dateStr = date.toLocaleDateString('sv-SE', {
    timeZone: 'Asia/Shanghai'
  });
  return dateStr === today;
}

/**
 * 获取友好的日期显示
 * @param dateStr 日期字符串
 */
export function getFriendlyDateDisplay(dateStr: string): string {
  const date = new Date(dateStr);
  const today = getChinaDate();
  const yesterday = getChinaDateStart(-1);
  
  const dateString = date.toLocaleDateString('sv-SE', {
    timeZone: 'Asia/Shanghai'
  });
  const todayString = today;
  const yesterdayString = yesterday.toLocaleDateString('sv-SE', {
    timeZone: 'Asia/Shanghai'
  });
  
  if (dateString === todayString) {
    return '今天';
  } else if (dateString === yesterdayString) {
    return '昨天';
  } else {
    // 使用中国时区显示
    return date.toLocaleDateString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      month: 'numeric',
      day: 'numeric'
    });
  }
}

/**
 * 计算两个时间之间的分钟差（基于中国时区）
 * @param currentTime 当前时间字符串
 * @param previousTime 之前时间字符串
 */
export function getMinutesDifference(currentTime: string, previousTime: string): number {
  const current = new Date(currentTime);
  const previous = new Date(previousTime);
  return Math.round((current.getTime() - previous.getTime()) / (1000 * 60));
}

/**
 * 获取月份的第一天（中国时区）
 * @param monthOffset 月份偏移量，0为当月，-1为上月
 */
export function getChinaMonthStart(monthOffset: number = 0): Date {
  const chinaToday = getChinaDate();
  const date = new Date(chinaToday);
  date.setMonth(date.getMonth() + monthOffset, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * 获取月份的最后一天（中国时区）
 * @param monthOffset 月份偏移量，0为当月，-1为上月
 */
export function getChinaMonthEnd(monthOffset: number = 0): Date {
  const chinaToday = getChinaDate();
  const date = new Date(chinaToday);
  date.setMonth(date.getMonth() + monthOffset + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
} 